import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, output, viewChildren } from '@angular/core';
import { ZuiThreeDotsModule } from '@zerops/zui/three-dots';
import { ZuiClaudeMarkComponent } from '@zerops/zui/claude-mark';
import { ZuiCodexMarkComponent } from '@zerops/zui/codex-mark';
import { ZuiAntigravityMarkComponent } from '@zerops/zui/antigravity-mark';
import { ZuiGrokMarkComponent } from '@zerops/zui/grok-mark';
import { ZuiCursorMarkComponent } from '@zerops/zui/cursor-mark';
// Leaf import (not the barrel): the barrel drags in the whole zerops-services
// component/YAML-builder graph, unrelated to this presentation-only lookup —
// same reasoning as code-server-overlay.bridge.ts's leaf import of this file.
import {
  AGENT_DISPLAY_NAMES,
  SUPPORTED_AGENT_TYPES,
  ZagentAgentType
} from '@zerops/zerops/core/zerops-services/zerops-services.model';
import { ZcpOnboardWizardService } from './zcp-onboard-wizard.service';

/**
 * Full-screen onboarding wizard layer (spec-welcome-mode.md §8), mounted once
 * at the app root — the presentation half of `ZcpOnboardWizardService`'s own
 * state machine (§8.1). No state of its own and no bridge access (§8.3): every
 * transition and the picked agent come from the service; the roster is the
 * static `SUPPORTED_AGENT_TYPES` registry rendered directly. This component
 * only renders the service's state and forwards user actions (`pickAgent`/
 * `hide` + the "Skip for now"/failure-state Continue exits) back onto it.
 */
@Component({
  selector: 'z-zcp-onboard-wizard',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ZuiThreeDotsModule,
    ZuiClaudeMarkComponent,
    ZuiCodexMarkComponent,
    ZuiAntigravityMarkComponent,
    ZuiGrokMarkComponent,
    ZuiCursorMarkComponent
  ],
  template: `
    @if (wizard.visible()) {
      <div class="__overlay">
        <div class="__inner" [class.__inner--wide]="wizard.state() === 'picking'">
          @switch (wizard.state()) {
            @case ('picking') {
              <h2 class="__title" id="zcp-onboard-wizard-title">Onboard your coding agent</h2>
              <p class="__sub">Pick an agent to authorize now — you'll drop straight into your editor when it's ready.</p>
              <div class="__roster" role="group" aria-labelledby="zcp-onboard-wizard-title">
                @for (agentId of supportedAgentTypes; track agentId) {
                  <button
                    type="button"
                    #tileRef
                    class="__agent-btn"
                    [class.__agent-btn--selected]="wizard.pickedAgentId() === agentId"
                    [attr.aria-pressed]="wizard.pickedAgentId() === agentId"
                    (click)="pickAgent(agentId)">
                    <span class="__mark" aria-hidden="true">
                      @switch (agentId) {
                        @case ('claude-code') { <zui-claude-mark /> }
                        @case ('codex')       { <zui-codex-mark /> }
                        @case ('antigravity') { <zui-antigravity-mark /> }
                        @case ('grok')        { <zui-grok-mark /> }
                        @case ('cursor')      { <zui-cursor-mark /> }
                      }
                    </span>
                    <span class="__name">{{ agentLabel(agentId) }}</span>
                    @if (isAuthorized(agentId)) {
                      <span class="__badge" aria-hidden="true">
                        <svg class="__badge-icon" viewBox="0 0 16 16" width="9" height="9" fill="none">
                          <path d="M3 8.2l3.1 3L13 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </span>
                      <span class="__visually-hidden">Already authorized</span>
                    }
                  </button>
                }
              </div>
              <button type="button" class="__skip" (click)="onExit()">Skip, take me to the dashboard</button>
            }
            @case ('authorizing') {
              <div class="__status" aria-live="polite">
                <span class="__mark __state-mark" aria-hidden="true">
                  @switch (wizard.pickedAgentId()) {
                    @case ('claude-code') { <zui-claude-mark /> }
                    @case ('codex')       { <zui-codex-mark /> }
                    @case ('antigravity') { <zui-antigravity-mark /> }
                    @case ('grok')        { <zui-grok-mark /> }
                    @case ('cursor')      { <zui-cursor-mark /> }
                  }
                </span>
                <zui-three-dots />
                <p class="__msg">Waiting for you to finish signing in…</p>
                <p class="__sub">{{ pickedAgentLabel() }} needs to be authorized before it can start.</p>
              </div>
            }
            @case ('launching') {
              <div class="__status" aria-live="polite">
                <span class="__mark __state-mark" aria-hidden="true">
                  @switch (wizard.pickedAgentId()) {
                    @case ('claude-code') { <zui-claude-mark /> }
                    @case ('codex')       { <zui-codex-mark /> }
                    @case ('antigravity') { <zui-antigravity-mark /> }
                    @case ('grok')        { <zui-grok-mark /> }
                    @case ('cursor')      { <zui-cursor-mark /> }
                  }
                </span>
                <zui-three-dots />
                <p class="__msg">Starting {{ pickedAgentLabel() }}…</p>
                <p class="__sub">Almost there, this only takes a moment.</p>
              </div>
            }
            @case ('failed') {
              <div class="__status" aria-live="polite">
                <span class="__mark __state-mark" aria-hidden="true">
                  @switch (wizard.pickedAgentId()) {
                    @case ('claude-code') { <zui-claude-mark /> }
                    @case ('codex')       { <zui-codex-mark /> }
                    @case ('antigravity') { <zui-antigravity-mark /> }
                    @case ('grok')        { <zui-grok-mark /> }
                    @case ('cursor')      { <zui-cursor-mark /> }
                  }
                </span>
                <p class="__msg">We couldn't reach your workspace.</p>
                <p class="__sub">Continue to your project — you can open the editor from there.</p>
              </div>
              <button type="button" class="__continue" (click)="onExit()">Continue</button>
            }
            @case ('promo') {
              @if (wizard.promoReveal(); as promo) {
                <div class="__status" aria-live="polite">
                  @switch (promo.state) {
                    @case ('redeemed') {
                      <p class="__promo-amount">
                        <span class="__promo-coin" aria-hidden="true">Z</span>{{ promoAmount() }}
                      </p>
                      <p class="__msg">promo credit added</p>
                      <p class="__sub">from your code <strong>{{ promo.code }}</strong></p>
                    }
                    @case ('parked') {
                      <p class="__msg">Your promo code is saved</p>
                      <p class="__sub">
                        <strong>{{ promo.code }}</strong> applies to your first top-up. You can do it now, or whenever suits you.
                      </p>
                    }
                    @case ('needs-top-up') {
                      <p class="__msg">Your promo code is waiting</p>
                      <p class="__sub">
                        <strong>{{ promo.code }}</strong> is redeemed as part of a top-up — start one below to apply it.
                      </p>
                    }
                    @case ('confirming') {
                      <zui-three-dots />
                      <p class="__msg">Payment confirmed</p>
                      <p class="__sub">
                        Waiting for the promo credit from <strong>{{ promo.code }}</strong> to land.
                        You can continue, it gets added either way.
                      </p>
                    }
                    @case ('awaiting-redeem') {
                      <p class="__msg">Your promo code is waiting</p>
                      <p class="__sub">
                        Redeem <strong>{{ promo.code }}</strong> from the Finances page whenever you like.
                      </p>
                    }
                  }
                </div>
                <button type="button" class="__continue" (click)="onPromoContinue()">Continue</button>
                @if (promo.state === 'parked' || promo.state === 'needs-top-up') {
                  <button type="button" class="__skip" (click)="onPromoTopUp(promo.code)">Top up now</button>
                }
              }
            }
            @case ('claiming') {
              <div class="__status" aria-live="polite">
                <zui-three-dots />
                <p class="__msg">Setting up your workspace…</p>
                <p class="__sub">This only takes a moment.</p>
              </div>
            }
          }
        </div>
      </div>
    }
  `,
  styles: [ `
    .__promo-amount {
      margin: 0 0 4px;
      font-size: 44px;
      line-height: 1.1;
      font-weight: 500;
    }

    .__promo-coin {
      margin-right: 3px;
      font-weight: 900;
      opacity: 0.6;
      color: #00daac;
    }

    .__overlay {
      position: fixed;
      inset: 0;
      // Band, deliberately: ABOVE the code-server embed (z-index 250,
      // code-server-overlay.feature.scss) so the layer covers the prewarming
      // vscode, and BELOW the CDK overlay container (z-index 1000) so the
      // auth dialog opens OVER the layer (spec-welcome-mode.md §8.1 —
      // "dispatches the existing auth dialog over the layer"). The old dumb
      // cover this evolved from used 100000 and never had to host a dialog;
      // at that value the dialog opens underneath and auth is unreachable.
      z-index: 500;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--z-app-background);
      animation: zcp-onboard-wizard-in 160ms ease-out;
    }

    .__inner {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: 0 24px;
      // width, not just max-width: the overlay centres this box, so without
      // an explicit width it collapses to the widest line of copy and the
      // tile row has nothing to lay out in (tiles then shrink to fit that
      // text instead of their own basis).
      width: 100%;
      max-width: 420px;
    }

    // Picking lays five fixed-width tiles out — needs more breathing room
    // than the short status copy the other states show. 640px fits the five
    // 108px tiles plus gaps on one row; below that the row wraps.
    .__inner--wide {
      max-width: 640px;
    }

    .__title {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
    }

    .__msg {
      margin: 22px 0 0;
      font-size: 15px;
      font-weight: 600;
    }

    .__sub {
      margin: 8px 0 0;
      font-size: 13px;
      opacity: 0.6;
      line-height: 1.5;
    }

    // Semantic/ARIA wrapper only — display: contents keeps its children
    // (mark/three-dots/msg/sub) participating directly in .__inner's flex
    // column exactly as if this element weren't here, so the existing
    // spacing rules above don't need a second, wrapper-aware set of rules.
    .__status {
      display: contents;
    }

    // Fixed-size tiles that wrap below 640px instead of shrinking — every
    // tile keeps the same geometry at any viewport, labels never wrap.
    .__roster {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 12px;
      margin: 32px 0 8px;
      width: 100%;
    }

    .__agent-btn {
      box-sizing: border-box;
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      width: 108px;
      padding: 18px 10px;
      border-radius: 12px;
      border: 1px solid var(--zcp-wizard-tile-border);
      background: var(--zcp-wizard-tile-bg);
      color: inherit;
      cursor: pointer;
      transition: background-color 120ms ease, border-color 120ms ease, transform 120ms ease;
    }

    // Scoped to devices with a real hover capability so touch doesn't get a
    // sticky "hovered" tile after a tap.
    @media (hover: hover) {
      .__agent-btn:hover {
        background: var(--zcp-wizard-tile-bg-hover);
        border-color: var(--zcp-wizard-tile-border-hover);
        transform: translateY(-2px);
      }
    }

    // Deliberately separate from hover: a ring, not a background tint, so
    // keyboard focus reads unambiguously even when the pointer is nowhere
    // near the tile.
    .__agent-btn:focus-visible {
      outline: 2px solid var(--zcp-wizard-focus-ring);
      outline-offset: 2px;
    }

    // The retained pick, re-rendered after an auth-dialog dismissal bounces
    // back to picking — visibly selected even with no pointer/keyboard
    // focus on it, so the user can see which tile they already chose.
    .__agent-btn--selected {
      background: var(--zcp-wizard-tile-bg-selected);
      border-color: var(--zcp-wizard-tile-border-selected);
    }

    .__mark {
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      color: inherit;
    }

    .__agent-btn .__mark {
      width: 40px;
      height: 40px;
      font-size: 36px;
    }

    // Status-screen hero mark (authorizing/launching/failed) — a consistent,
    // larger size shared by all three so they read as one family regardless
    // of which one a given run passes through.
    .__state-mark {
      width: clamp(40px, 9vw, 56px);
      height: clamp(40px, 9vw, 56px);
      font-size: clamp(36px, 8vw, 50px);
    }

    .__name {
      font-size: 13px;
      font-weight: 600;
      text-align: center;
      max-width: 100%;
    }

    // Quiet corner check for an already-authorized agent (§8.2) — absolutely
    // positioned so its presence never changes the tile's box geometry.
    .__badge {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--zcp-wizard-badge-bg);
      border: 1px solid var(--zcp-wizard-badge-border);
      color: var(--zcp-wizard-badge-icon);
    }

    .__badge-icon {
      display: block;
    }

    // Standard offscreen-but-announced clip — conveys "Already authorized"
    // to assistive tech without the decorative badge glyph (aria-hidden)
    // having to carry it.
    .__visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .__skip {
      margin-top: 24px;
      padding: 8px 14px;
      background: none;
      border: none;
      color: inherit;
      opacity: 0.55;
      font-size: 13px;
      cursor: pointer;
      transition: opacity 120ms ease;
    }

    .__skip:hover {
      opacity: 0.85;
      text-decoration: underline;
    }

    .__continue {
      margin-top: 20px;
      padding: 11px 26px;
      border-radius: 8px;
      border: none;
      background: var(--zcp-wizard-cta-bg);
      color: var(--zcp-wizard-cta-text);
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 150ms ease;
    }

    @media (hover: hover) {
      .__continue:hover {
        background: var(--zcp-wizard-cta-bg-hover);
      }
    }

    .__continue:active {
      background: var(--zcp-wizard-cta-bg-hover);
    }

    .__continue:focus-visible {
      outline: 2px solid var(--zcp-wizard-focus-ring);
      outline-offset: 2px;
    }

    @keyframes zcp-onboard-wizard-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  ` ]
})
export class ZcpOnboardWizardComponent {
  wizard = inject(ZcpOnboardWizardService);

  /**
   * Fired whenever this component drops its own layer (`wizard.hide()`)
   * without an agent having launched: the failure state's Continue button
   * and "Skip, take me to the dashboard" from picking. §8.1 also requires
   * closing the code-server overlay and landing on project detail — reach
   * this component deliberately doesn't have (no bridge access, §8.3);
   * whoever mounts this component wires that part identically for both
   * exits.
   */
  continueClicked = output<void>();

  /**
   * "Top up now" from the promo reveal. Deliberately NOT an exit: this layer
   * sits at z-index 500 so the CDK overlay opens over it (the same band the
   * agent auth dialog relies on), which means the top-up dialog can be hosted
   * without dropping the wizard. Dismissing the dialog returns to the reveal
   * and Continue still leads to the roster — there is no way back into agent
   * picking once this layer is gone, so it must not be closed here.
   */
  topUpClicked = output<string>();

  /** The static, always-available roster (§8.1) — rendered directly, never waited on. */
  supportedAgentTypes = SUPPORTED_AGENT_TYPES;

  // Angular's signal-query decorators reject TS `#`-private fields (they
  // must be public/protected/private via the `private` keyword) — plain
  // `private` here, not `#`, is a compiler constraint, not a style choice.
  private tileRefs = viewChildren<ElementRef<HTMLButtonElement>>('tileRef');

  /**
   * The amount, formatted here rather than through `zui-zerops-price`: that
   * component's module drags in `ZefPipesModule`, exactly the kind of graph the
   * leaf-import note at the top of this file exists to keep out — it breaks
   * this component's own spec. Arrives as a decimal string, so a plain
   * two-decimal render is all it needs.
   */
  promoAmount = computed(() => {
    const amount = this.wizard.promoReveal()?.amount;
    if (!amount) { return ''; }

    const parsed = Number(amount);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : amount;
  });

  pickedAgentLabel = computed(() => {
    const id = this.wizard.pickedAgentId();
    return id ? this.agentLabel(id) : '';
  });

  /**
   * Focus follows the wizard's own state, never a template event: entering
   * `picking` with a pick already set can only be the auth-dialog-dismissed
   * bounce-back (`enterPicking()` always clears the pick, so that
   * combination has no other cause) and refocuses the retained tile.
   * Effects run after change detection has settled the DOM for the new
   * state, so the target element is always present by the time this runs —
   * no `afterNextRender` needed. Side-effect only: never writes a signal.
   */
  constructor() {
    effect(() => {
      const state = this.wizard.state();
      const pickedId = this.wizard.pickedAgentId();

      if (state === 'picking' && pickedId) {
        this.focusTile(pickedId);
      }
    });
  }

  agentLabel(agentId: string): string {
    return AGENT_DISPLAY_NAMES[agentId as ZagentAgentType] ?? agentId;
  }

  /** Whether the roster entry is already authorized (§8.2) — drives the tile's quiet corner badge. */
  isAuthorized(agentId: string): boolean {
    return this.wizard.authorizedAgentIds().has(agentId);
  }

  pickAgent(agentId: string): void {
    this.wizard.pickAgent(agentId);
  }

  onPromoContinue(): void {
    this.wizard.dismissPromoReveal();
  }

  onPromoTopUp(code: string): void {
    this.topUpClicked.emit(code);
  }

  /**
   * The two non-agent exits — "Skip, take me to the dashboard" (picking) and
   * Continue (failed) — both converge here: drop this component's own layer,
   * then let whoever mounts it close the code-server overlay and navigate to
   * project detail.
   */
  onExit(): void {
    this.wizard.hide();
    this.continueClicked.emit();
  }

  private focusTile(agentId: string): void {
    const idx = this.supportedAgentTypes.indexOf(agentId as ZagentAgentType);
    this.tileRefs()[idx]?.nativeElement.focus();
  }
}
